import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import PropTypes from 'prop-types'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import Input from '../../../components/forms/Input'
import i18n from 'simple-react-i18n'
import { getDate } from '../../../utils/DateUtil'
import useTitle from '../../../utils/customHook/useTitle'
import useActions from '../../../utils/customHook/useActions'
import { StyledFieldSet } from '../../../components/StyledElements'
import PerimetersThunk from '../../action/PerimetersThunk'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import Textarea from '../../../components/forms/Textarea'
import SimpleDatePicker from '../../../components/forms/SimpleDatePicker'
import PerimetersOwnersTable from './components/PerimetersOwnersTable'
import { ButtonMUI } from '../../../components/styled/Buttons'
import ToastrAction from 'toastr/actions/ToastrAction'
import ConfirmModal from '../../../components/modal/ConfirmModal'
import AdministrationAction from '../../../administration/actions/AdministrationAction'
import { searchAllCharacters } from '../../../utils/StringUtil'
import FiltersPanel from 'administration/components/filters/FiltersPanel'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import ProgressCard from 'components/card/ProgressCard'
import { isNil, keys } from 'lodash'
import PerimetersAction from '../../action/PerimetersAction'
import ContactAction from '../../../referencial/components/contact/actions/ContactAction'
import ContributorAction from '../../../referencial/components/contributor/actions/ContributorAction'
import DeedDto from 'perimeters/dto/DeedDto'
import { CardTable } from 'components/datatable/NewTable'
import { DefaultDialogTitle } from 'components/styled/Dialog'

const DEEDS_HEADER = ['number', 'comment', 'date', 'publicationDate', 'owner']

const DeedDialog = ({
    open = false,
    deed = {},
    setDeed = () => {},
    setDeedActionIsOpen = () => {},
    readMode = true,
}) => {
    const [deedVolume, setDeedVolume] = useState()
    const [deedDate, setDeedDate] = useState()
    const [deedPublishDate, setDeedPublishDate] = useState()
    const [deedComment, setDeedComment] = useState()
    const [deedPublicationNumber, setDeedPublicationNumber] = useState()
    const [deedRecoveryId, setDeedRecoveryId] = useState()

    const [deedOwners, setDeedOwners] = useState([])

    const dispatch = useDispatch()

    useEffect(() => {
        if (deed.mortgageId) {
            setDeedVolume(deed.volume)
            setDeedDate(deed.deedDate)
            setDeedPublishDate(deed.publicationDate)
            setDeedComment(deed.comment)
            setDeedPublicationNumber(deed.publicationNumber)
            setDeedRecoveryId(deed.recoveryId)

            dispatch(PerimetersAction.getDeedOwners(deed.mortgageId)).then(ownersOfDeed => {
                setDeedOwners(ownersOfDeed)
            })
        }

        return () => {
            setDeedVolume(undefined)
            setDeedDate(undefined)
            setDeedPublishDate(undefined)
            setDeedComment(undefined)
            setDeedPublicationNumber(undefined)
            setDeedRecoveryId(undefined)
        }
    }, [deed])

    const canBeCreated = deedVolume || !isNil(deedDate) || !isNil(deedPublishDate) || deedComment || deedPublicationNumber || deedRecoveryId

    const onClose = () => {
        setDeed({})
        setDeedActionIsOpen(false)
    }

    return (
        <Dialog
            fullWidth
            maxWidth='lg'
            open={open}
            slotProps={{
                paper: {
                    sx: {
                        minHeight: '90vh',
                        maxHeight: '90vh',
                    },
                },
            }}
        >
            <DefaultDialogTitle
                title={deed?.mortgageId ? i18n.updateDeed : i18n.createDeed}
                onClose={onClose}
            />
            <DialogContent>
                <StyledFieldSet style={{ padding: '1.5rem' }}>
                    <Grid container size={12} spacing={2}>
                        {!!deed?.publicationNumber && (
                            <Grid size={6}>
                                <Input
                                    title={i18n.number}
                                    value={deed?.mortgageId ?? ''}
                                    disabled
                                />
                            </Grid>
                        )}
                        <Grid size={deed?.publicationNumber ? 6 : 12}>
                            <Input
                                title={i18n.volume}
                                value={deedVolume ?? ''}
                                onChange={setDeedVolume}
                                clearFunction
                                disabled={readMode}
                            />
                        </Grid>
                    </Grid>
                    <Grid container size={12} spacing={2}>
                        <Grid size={6}>
                            <SimpleDatePicker
                                label={i18n.date}
                                value={deedDate}
                                onChange={setDeedDate}
                                disabled={readMode}
                            />
                        </Grid>
                        <Grid size={6}>
                            <SimpleDatePicker
                                label={i18n.publicationDate}
                                value={deedPublishDate}
                                onChange={setDeedPublishDate}
                                disabled={readMode}
                            />
                        </Grid>
                    </Grid>
                    <Grid size={12}>
                        <Textarea
                            title={i18n.comment}
                            value={deedComment}
                            onChange={setDeedComment}
                            disabled={readMode}
                        />
                    </Grid>
                    <Grid container size={12} spacing={2} sx={{ paddingTop: '0.5rem' }}>
                        <Grid size={6}>
                            <Input
                                title={i18n.publicationNumber}
                                value={deedPublicationNumber ?? ''}
                                onChange={setDeedPublicationNumber}
                                clearFunction
                                disabled={readMode}
                            />
                        </Grid>
                        <Grid size={6}>
                            <Input
                                title={i18n.recoveryId}
                                value={deedRecoveryId ?? ''}
                                onChange={setDeedRecoveryId}
                                clearFunction
                                disabled={readMode}
                            />
                        </Grid>
                    </Grid>
                </StyledFieldSet>
                {!!deed?.mortgageId && (
                    <Grid size={12} sx={{ paddingTop: '1rem' }}>
                        {keys(deedOwners).length ? (
                            <PerimetersOwnersTable
                                privateOwners={deedOwners?.privateOwners}
                                companyOwners={deedOwners?.companyOwners}
                                readMode={readMode}
                                onValidate={owners => owners.forEach(owner => dispatch(PerimetersThunk.addDeedOwner(owner)))}
                            />
                        ) : (
                            <ProgressCard indeterminate />
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setDeed({})
                        setDeedActionIsOpen(false)
                    }}
                    variant='outlined'
                >
                    {i18n.cancel}
                </Button>
                <ButtonMUI
                    onClick={() => {
                        if (deed?.mortgageId) {
                            dispatch(PerimetersAction.updateMortgage({
                                id: deed.mortgageId,
                                date: deedDate,
                                comment: deedComment,
                                publicationDate: deedPublishDate,
                                volume: deedVolume,
                                publicationNumber: deedPublicationNumber,
                                recoveryId: deedRecoveryId,
                            }))
                        } else if (canBeCreated) {
                            dispatch(PerimetersAction.createMortgage({
                                date: deedDate,
                                comment: deedComment,
                                publicationDate: deedPublishDate,
                                volume: deedVolume,
                                publicationNumber: deedPublicationNumber,
                                recoveryId: deedRecoveryId,
                            }))
                        } else {
                            dispatch(ToastrAction.error(i18n.pleaseEnterAtLeastOneParameter))
                        }

                        setDeedActionIsOpen(false)
                    }}
                    disabled={readMode}
                    variant='contained'
                    color='primary'
                >
                    {i18n.save}
                </ButtonMUI>
            </DialogActions>
        </Dialog>
    )
}

DeedDialog.propTypes = {
    open: PropTypes.bool,
    deed: PropTypes.instanceOf(DeedDto),
    setDeed: PropTypes.func,
    setDeedActionIsOpen: PropTypes.func,
    readMode: PropTypes.bool,
}

const MortgagesTable = ({
    mortgages = [],
    readMode,
    setDeed,
    setDeedActionIsOpen,
}) => {
    const dispatch = useDispatch()

    const {
        selectedSearchValues,
    } = useSelector(store => ({
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
    }), shallowEqual)

    const [deletedDeed, setDeletedDeed] = useState()
    const [deletedIsOpen, setDeletedIsOpen] = useState()

    const { perimetersDeeds: searchValues } = selectedSearchValues
    const {
        searchValue,
    } = searchValues ?? {}

    const searchValueCharacters = searchAllCharacters(searchValue)

    const deedsFormated = mortgages
        .filter(deed => (!searchValue || (deed.publicationNumber?.toString().includes(searchValue)) || (searchAllCharacters(deed.comment).includes(searchValueCharacters))))
        .map(deed => {
            return {
                ...deed,
                number: deed.publicationNumber,
                date: getDate(deed.deedDate),
                publicationDate: getDate(deed.publicationDate),
                owner: deed.ownersNumber > 0 ? (<AccountCircleIcon/>) : '',
            }
        })

    return (
        <>
            <ConfirmModal
                isOpen={deletedIsOpen}
                title={i18n.sureDeleteDeed}
                onValidate={() => {
                    dispatch(PerimetersAction.deleteMortgage(deletedDeed)).then(res => {
                        if (res.type !== 'deleteDeed/rejected') {
                            setDeletedDeed(undefined)
                            setDeletedIsOpen(false)
                        }
                    })
                }}
                onClose={() => {
                    setDeletedDeed(undefined)
                    setDeletedIsOpen(false)
                }}
            />
            <CardTable
                title={i18n.deeds}

                rows={deedsFormated}
                headers={DEEDS_HEADER}
                lineActions={[{
                    icon: 'delete',
                    onClick: d => {
                        setDeletedDeed(d?.mortgageId)
                        setDeletedIsOpen(true)
                    },
                    displayed: !readMode,
                }]}

                onClickRow={d => {
                    setDeed(d)
                    setDeedActionIsOpen(true)
                }}
                rowsPerPageOptions={nbPerPageLabel}
            />
        </>
    )
}

MortgagesTable.propTypes = {
    mortgages: PropTypes.arrayOf(DeedDto),
    readMode: PropTypes.bool,
    setDeed: PropTypes.func,
    setDeedActionIsOpen: PropTypes.func,
}

const PerimetersDeeds = () => {
    const dispatch = useDispatch()

    const {
        selectedSearchValues,
        mortgages,
    } = useSelector(store => ({
        selectedSearchValues: store.AdministrationReducer.selectedSearchValues,
        mortgages: store.PerimetersReducer.mortgages,
    }), shallowEqual)

    const [searchValue, setSearchValue] = useState(selectedSearchValues.perimetersFolder?.searchValue ?? '')

    const [readMode, setReadMode] = useState(true)
    const [deed, setDeed] = useState({})
    const [deedActionsIsOpen, setDeedActionIsOpen] = useState(false)

    const onLaunchSearch = () => {
        dispatch(AdministrationAction.setCache('perimetersDeeds', { searchValue }))
        dispatch(AdministrationAction.setSelectedSearchValues('perimetersDeeds', { searchValue }))
    }

    useTitle(() => [{
        title: i18n.perimeters,
        href: 'perimeter',
    }, {
        title: i18n.deeds,
        href: 'deeds',
    }], [])

    const { isLoaded, progress } = useProgressDispatch(() => [
        dispatch(PerimetersThunk.getMortgages()),
        dispatch(ContactAction.fetchContacts()),
        dispatch(ContributorAction.fetchContributors()),
    ], [])

    useEffect(() => {
        onLaunchSearch()
    }, [searchValue])

    useActions(() => !readMode ? ({
        cancel: () => setReadMode(true),
        new: () => {
            setDeed({})
            setDeedActionIsOpen(true)
        },
    }) : ({
        export: () => {
            return {
                data: mortgages.map(d => ({
                    number: { value: d.publicationNumber },
                    comment: { value: d.comment },
                    date: { value: getDate(d.deedDate) },
                    publicationDate: { value: getDate(d.publicationDate) },
                    owner: { value: d.ownersNumber.toString() },
                    headers: DEEDS_HEADER,
                })),
                exportType: 'xlsx',
                titleFile: i18n.mortgages,
            }
        },
        edit: () => setReadMode(false),
    }), [readMode, mortgages])

    return (
        <Box sx={{ margin: '1rem 1rem 1rem 0.5rem' }}>
            <DeedDialog
                open={deedActionsIsOpen}
                deed={deed}
                setDeed={setDeed}
                setDeedActionIsOpen={setDeedActionIsOpen}
                readMode={readMode}
            />
            <FiltersPanel
                defaultFilter={{ searchValue }}
                onValidate={filter => setSearchValue(filter.searchValue)}
            />
            <Box sx={{ paddingTop: '1rem' }}>
                {isLoaded ? (
                    <MortgagesTable
                        readMode={readMode}
                        mortgages={mortgages}
                        setDeed={setDeed}
                        setDeedActionIsOpen={setDeedActionIsOpen}
                    />
                ) : <ProgressCard progress={progress} />}
            </Box>
        </Box>
    )
}

export default PerimetersDeeds